import type { DataDictionary, DataDictionarySource } from "./data-dictionary";
import {
  createComponentsIndex,
  createDataDictionaryFieldsIndex,
  createMessagesByType,
  loadDictionary,
  newDataDictionary,
} from "./data-dictionary";
import { postDictionaryChange } from "./dictionary-broadcast";

const STORAGE_KEY = "fix-parser-dictionaries";

export interface StoredDictionary {
  id: string;
  filename: string;
  content: string;
  savedAt: number;
}

export const loadStoredDictionaries = (): StoredDictionary[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as StoredDictionary[];
  } catch {
    return [];
  }
};

export const saveStoredDictionary = (
  filename: string,
  content: string,
):
  | { ok: true; entry: StoredDictionary }
  | { ok: false; error: "quota" | "unknown" } => {
  try {
    const entry: StoredDictionary = {
      id: crypto.randomUUID(),
      filename,
      content,
      savedAt: Date.now(),
    };
    const current = loadStoredDictionaries();
    localStorage.setItem(STORAGE_KEY, JSON.stringify([entry, ...current]));
    postDictionaryChange({
      type: "added",
      id: entry.id,
      filename: entry.filename,
    });
    return { ok: true, entry };
  } catch (e) {
    if (e instanceof DOMException && e.name === "QuotaExceededError") {
      return { ok: false, error: "quota" };
    }
    return { ok: false, error: "unknown" };
  }
};

export const deleteStoredDictionary = (id: string): void => {
  try {
    const current = loadStoredDictionaries();
    const removed = current.find((d) => d.id === id);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(current.filter((d) => d.id !== id)),
    );
    if (removed) {
      postDictionaryChange({
        type: "removed",
        id: removed.id,
        filename: removed.filename,
      });
    }
  } catch {
    // silently ignore
  }
};

export const restoreStoredDictionary = (
  entry: StoredDictionary,
): { ok: true } | { ok: false; error: "quota" | "unknown" } => {
  try {
    const current = loadStoredDictionaries();
    if (current.some((d) => d.id === entry.id)) {
      return { ok: true };
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify([entry, ...current]));
    postDictionaryChange({
      type: "added",
      id: entry.id,
      filename: entry.filename,
    });
    return { ok: true };
  } catch (e) {
    if (e instanceof DOMException && e.name === "QuotaExceededError") {
      return { ok: false, error: "quota" };
    }
    return { ok: false, error: "unknown" };
  }
};

export const buildDataDictionary = (
  filename: string,
  content: string,
  source: DataDictionarySource,
  id?: string,
): DataDictionary => {
  const parseResult = loadDictionary(content);
  if (!parseResult.success) {
    const dd = newDataDictionary();
    dd.id = id;
    dd.source = source;
    dd.dataSource = {
      filename,
      content,
      error: parseResult.error?.message || "Failed to parse dictionary",
    };
    return dd;
  }

  const dd = newDataDictionary();
  dd.id = id;
  dd.source = source;
  dd.fields = parseResult.data?.fields || [];
  dd.components = parseResult.data?.components || [];
  dd.messages = parseResult.data?.messages || [];
  dd.fieldsIndex = createDataDictionaryFieldsIndex(dd.fields);
  dd.componentsIndex = createComponentsIndex(dd.components);
  dd.messagesByType = createMessagesByType(dd.messages);
  const warnings = parseResult.data?.warnings ?? [];
  dd.dataSource = {
    filename,
    content,
    ...(warnings.length > 0 ? { warnings } : {}),
  };
  dd.enabled = true;
  return dd;
};

export const loadStoredDictionaryAsDataDictionary = (
  entry: StoredDictionary,
): DataDictionary =>
  buildDataDictionary(entry.filename, entry.content, "local", entry.id);

// Synchronous, non-cryptographic content fingerprint used to detect whether a
// URL-embedded dictionary matches one already saved locally. FNV-1a 32-bit is
// fine here — we tolerate the occasional miss (treats local dict as external).
export const hashDictionaryContent = (content: string): string => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < content.length; i++) {
    hash ^= content.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

export const findStoredDictionaryByContent = (
  content: string,
): StoredDictionary | undefined => {
  const target = hashDictionaryContent(content);
  return loadStoredDictionaries().find(
    (d) => hashDictionaryContent(d.content) === target && d.content === content,
  );
};
